import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Popover, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TranscriberResponseDto } from '@shared/services/apiService/apiService';
import {
  formatTranscriberLabel,
  transcriberMatchesProviderQuery,
} from '@shared/utils/transcriberDisplay';
import { SELECT_VOICES_LIST_MAX_HEIGHT, useStyles } from '@widgets/CompanyVoices/SelectVoicesInput/styles';

type SelectTranscribersInputProps = {
  transcribers: TranscriberResponseDto[] | undefined;
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  disabled?: boolean;
  portalDropdown?: boolean;
};

const MIN_LIST_HEIGHT = 120;

export const SelectTranscribersInput: React.FC<SelectTranscribersInputProps> = ({
  transcribers,
  selectedIds,
  onChange,
  disabled,
  portalDropdown = false,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const controlRef = useRef<HTMLDivElement | null>(null);
  const dropdownPortalRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const ignoreOutsideCloseRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [listMaxHeight, setListMaxHeight] = useState(SELECT_VOICES_LIST_MAX_HEIGHT);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const transcriberById = useMemo(() => {
    const map = new Map<string, TranscriberResponseDto>();
    (transcribers ?? []).forEach((item) => map.set(item.id, item));
    return map;
  }, [transcribers]);

  const selectedTranscribers = useMemo(
    () =>
      selectedIds
        .map((id) => transcriberById.get(id))
        .filter((item): item is TranscriberResponseDto => item !== undefined),
    [selectedIds, transcriberById],
  );

  const filteredTranscribers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return transcribers ?? [];
    return (transcribers ?? []).filter((item) => {
      const label = formatTranscriberLabel(item).toLowerCase();
      return (
        label.includes(q) ||
        transcriberMatchesProviderQuery(item, q) ||
        item.language.toLowerCase().includes(q)
      );
    });
  }, [transcribers, query]);

  const placeholder = t('superAdminMainPage.companies.selectTranscribersInput.placeholder');
  const showInputPlaceholder = selectedTranscribers.length === 0 && !query;

  const closeDropdown = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
  };

  const updateListMaxHeight = useCallback(() => {
    if (!controlRef.current) return;
    const rect = controlRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 16;
    setListMaxHeight(Math.max(MIN_LIST_HEIGHT, Math.min(SELECT_VOICES_LIST_MAX_HEIGHT, spaceBelow)));
  }, []);

  useEffect(() => {
    if (!open) return;
    updateListMaxHeight();
    window.addEventListener('resize', updateListMaxHeight);
    window.addEventListener('scroll', updateListMaxHeight, true);
    return () => {
      window.removeEventListener('resize', updateListMaxHeight);
      window.removeEventListener('scroll', updateListMaxHeight, true);
    };
  }, [open, updateListMaxHeight]);

  useEffect(() => {
    if (filteredTranscribers.length === 0) {
      setActiveIndex(-1);
      return;
    }
    if (activeIndex >= filteredTranscribers.length || activeIndex < 0) {
      setActiveIndex(0);
    }
  }, [filteredTranscribers, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (ignoreOutsideCloseRef.current) return;
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (dropdownPortalRef.current?.contains(target)) return;
      closeDropdown();
    };
    const listenerId = window.setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown);
    }, 0);
    return () => {
      window.clearTimeout(listenerId);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  const toggleTranscriber = (transcriberId: string) => {
    if (selectedSet.has(transcriberId)) {
      onChange(selectedIds.filter((id) => id !== transcriberId));
      return;
    }
    onChange([...selectedIds, transcriberId]);
    setQuery('');
    inputRef.current?.focus();
  };

  const removeTranscriber = (event: React.MouseEvent, transcriberId: string) => {
    event.stopPropagation();
    onChange(selectedIds.filter((id) => id !== transcriberId));
    inputRef.current?.focus();
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDropdown();
      inputRef.current?.blur();
      return;
    }
    if (event.key === 'Backspace' && !query && selectedIds.length > 0) {
      onChange(selectedIds.slice(0, -1));
      return;
    }
    if (!filteredTranscribers.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((prev) => (prev + 1) % filteredTranscribers.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((prev) => (prev - 1 + filteredTranscribers.length) % filteredTranscribers.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0) toggleTranscriber(filteredTranscribers[activeIndex].id);
    }
  };

  const handleControlMouseDown = (event: React.MouseEvent) => {
    if (disabled) return;
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    ignoreOutsideCloseRef.current = true;
    openDropdown();
    inputRef.current?.focus();
    window.requestAnimationFrame(() => {
      ignoreOutsideCloseRef.current = false;
    });
  };

  const listContent = (
    <Box
      id="select-transcribers-listbox"
      role="listbox"
      className={classes.list}
      aria-multiselectable
      style={{ maxHeight: listMaxHeight }}>
      {filteredTranscribers.length === 0 ? (
        <Box className={classes.empty}>{t('superAdminMainPage.companies.selectTranscribersInput.empty')}</Box>
      ) : (
        filteredTranscribers.map((item, idx) => {
          const isSelected = selectedSet.has(item.id);
          const isActive = activeIndex === idx;
          return (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={classNames(classes.row, {
                [classes.rowActive]: isActive && !isSelected,
                [classes.rowSelected]: isSelected,
              })}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => toggleTranscriber(item.id)}>
              <span className={classes.rowTitle}>{formatTranscriberLabel(item)}</span>
              {isSelected ? <CheckIcon className={classes.checkIcon} /> : null}
            </button>
          );
        })
      )}
    </Box>
  );

  return (
    <Box ref={rootRef} className={classNames(classes.root, { [classes.rootOpen]: open && !portalDropdown })}>
      <Box
        ref={controlRef}
        className={classNames(classes.control, {
          [classes.controlFocused]: open,
          [classes.controlDisabled]: disabled,
        })}
        onMouseDown={handleControlMouseDown}>
        {selectedTranscribers.map((item) => (
          <span key={item.id} className={classes.chip}>
            <span className={classes.chipLabel}>{formatTranscriberLabel(item)}</span>
            <button
              type="button"
              className={classes.chipRemove}
              disabled={disabled}
              aria-label={t('superAdminMainPage.companies.selectTranscribersInput.removeTranscriber', {
                name: formatTranscriberLabel(item),
              })}
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => removeTranscriber(event, item.id)}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="select-transcribers-listbox"
          aria-autocomplete="list"
          disabled={disabled}
          className={classes.inlineInput}
          placeholder={showInputPlaceholder ? placeholder : undefined}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            openDropdown();
          }}
          onFocus={openDropdown}
          onKeyDown={onInputKeyDown}
        />
        <span className={classes.chevron}>
          <KeyboardArrowDownIcon fontSize="small" />
        </span>
      </Box>

      {open && !disabled && portalDropdown ? (
        <Popover
          open
          anchorEl={controlRef.current}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          marginThreshold={0}
          slotProps={{
            root: {
              sx: { zIndex: theme.zIndex.modal + 1 },
            },
            paper: {
              className: classes.dropdownPortalPaper,
              style: { width: controlRef.current?.offsetWidth },
            },
          }}>
          <Box
            ref={dropdownPortalRef}
            onMouseDown={(event) => event.preventDefault()}
            sx={{ width: '100%' }}>
            {listContent}
          </Box>
        </Popover>
      ) : null}

      {open && !disabled && !portalDropdown ? (
        <Box className={classes.dropdown} onMouseDown={(event) => event.preventDefault()}>
          {listContent}
        </Box>
      ) : null}
    </Box>
  );
};
